// Sidebar — left-rail navigation for authenticated users. Shows view
// items based on role (admin/lawyer/legalops/etc.) plus the
// notifications bell, current user info, and sign-out button.

import { T } from "../theme";
import type { UserRow } from "../types/api";
import { Btn } from "./primitives";
import { ClientPicker } from "./ClientPicker";
import { OrgBadge } from "./OrgBadge";
import { useClientScope } from "../contexts/ClientScopeContext";

export const Sidebar = ({ view = '', onNav = undefined, user = null, onLogout = undefined, notifCount = 0 }: { view?: string; onNav?: any; user?: UserRow | null; onLogout?: any; notifCount?: number }) => {
  const scope = useClientScope();
  // Sidebar items are gated by nav_permissions from the auth response
  // (RBAC Phase 1). Legacy fallback: if nav_permissions is missing — e.g.
  // the user hasn't re-authed since the backend migration, or role_id is
  // still null — fall back to the role-based visibility the site has used
  // historically. Removing the fallback is safe once all active sessions
  // have re-authed; leaving it in during rollout avoids locking anyone out.
  const nav = user?.nav_permissions;
  const role = user?.role || '';
  const see = (key: string, legacyRoles: string[] | null = null): boolean => {
    if (nav && typeof nav === 'object') return !!nav[key];
    if (legacyRoles === null) return true; // item always visible
    return legacyRoles.includes(role);
  };
  const items = [
    ...(see('dashboard', ['admin','lawyer','legalops','commercial_ops','contract_manager','requester','executive']) ? [{ id: "dashboard", label: "Dashboard", icon: "▣" }] : []),
    ...(see('requests', null) ? [{ id: "requests", label: "Requests", icon: "◫" }] : []),
    ...(see('self_service', ["requester","admin","legalops","commercial_ops","lawyer","contract_manager"]) ? [{ id: "selfservice", label: "Self-Service", icon: "◈" }] : []),
    ...(see('drafting', ["admin","legalops","lawyer","contract_manager","commercial_ops"]) ? [{ id: "drafting", label: "Drafting", icon: "✏" }] : []),
    ...(see('my_queue', ["admin","legalops","lawyer","contract_manager","commercial_ops","requester"]) ? [{ id: "queue", label: "My Queue", icon: "◧" }] : []),
    // My Desk — in-house edition only, lawyer-role only. Sidebar gate
    // mirrors the backend gate (/api/dashboard/my-desk returns 404 outside
    // these conditions). Backend gate is authoritative.
    ...((scope?.edition === 'in_house' && role === 'lawyer') ? [{ id: "mydesk", label: "My Desk", icon: "▤" }] : []),
    ...(see('repository', ["admin","legalops","commercial_ops","lawyer","executive"]) ? [{ id: "repo", label: "Repository", icon: "◨" }] : []),
    ...(see('ai_import', ["admin","legalops","commercial_ops"]) ? [{ id: "import", label: "AI Import", icon: "◬" }] : []),
    ...(see('search', ['admin','lawyer','legalops','commercial_ops','contract_manager','requester','executive']) ? [{ id: "search", label: "Search", icon: "⌕" }] : []),
    ...(see('ai_chat', ['admin','lawyer','legalops','commercial_ops','contract_manager','requester','executive']) ? [{ id: "aichat", label: "AI Chat", icon: "◆" }] : []),
    ...(see('execution_queue', ["admin","legalops","commercial_ops"]) ? [{ id: "execqueue", label: "Execution Queue", icon: "✍" }] : []),
    ...(see('reports', ["admin","legalops","commercial_ops","lawyer","contract_manager","executive"]) ? [{ id: "reports", label: "Reports", icon: "📊" }] : []),
    ...(see('administration', ["admin"]) ? [{ id: "admin", label: "Administration", icon: "◉" }] : []),
    // Platform Console — operator-only (admin in the bootstrap tenant). Shown
    // only when the auth payload flags is_operator; the server-side gate
    // (routes/platformAdmin.js) is authoritative regardless.
    ...(user?.is_operator ? [{ id: "platform", label: "Platform", icon: "⬢" }] : []),
    // Setup Wizard — operator-only guided onboarding. Same is_operator gate as
    // Platform; the server-side /api/platform gate is authoritative regardless.
    ...(user?.is_operator ? [{ id: "wizard", label: "Setup Wizard", icon: "✧" }] : []),
    { id: "help", label: "User Guide", icon: "?" },
  ];
  return (
    <div style={{ width: 280, minHeight: "100vh", backgroundColor: "#FFFFFF", borderRight: `1px solid ${T.brd}`, display: "flex", flexDirection: "column", flexShrink: 0 }}>
      <div style={{ padding: "24px 20px 20px", borderBottom: `1px solid ${T.brd}` }}>
        <img src="/logo.png" alt="KontractAI" style={{ height: 60, width: "auto", objectFit: "contain" }} />
      </div>
      <nav style={{ flex: 1, padding: "12px 8px" }}>
        {items.map((i: any) => (
          <button key={i.id} onClick={() => onNav?.(i.id)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "11px 14px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 15, fontWeight: view === i.id ? 600 : 400, color: view === i.id ? T.tx : T.txM, backgroundColor: view === i.id ? T.accS : "transparent", textAlign: "left", fontFamily: "inherit", marginBottom: 2 }}>
            <span style={{ fontSize: 15, opacity: .7 }}>{i.icon}</span>{i.label}
          </button>
        ))}
      </nav>
      <button onClick={() => onNav?.("notifications")} style={{ display: "flex", alignItems: "center", gap: 10, margin: "0 8px 4px", padding: "11px 14px", borderRadius: 6, border: "none", cursor: "pointer", fontSize: 15, color: T.txM, backgroundColor: view === "notifications" ? T.accS : "transparent", textAlign: "left", fontFamily: "inherit" }}>
        <span>🔔</span> Notifications
        {notifCount > 0 && <span style={{ marginLeft: "auto", fontSize: 11, padding: "2px 8px", borderRadius: 8, backgroundColor: T.red, color: "#fff", fontWeight: 700 }}>{notifCount}</span>}
      </button>
      <div style={{ padding: "14px 18px", borderTop: `1px solid ${T.brd}` }}>
        <ClientPicker />
        <div style={{ fontSize: 14, color: T.tx, fontWeight: 600 }}>{user?.name}</div>
        <div style={{ fontSize: 12, color: T.txD, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ textTransform: "capitalize" }}>{user?.role?.replace("legalops", "Legal Ops").replace("commercial_ops", "Commercial Ops").replace("contract_manager", "Contract Manager").replace("business_reviewer", "Business Reviewer")}</span>
          <OrgBadge org_type={scope.organization?.org_type} size="xs" />
        </div>
        <Btn v="ghost" sz="sm" onClick={onLogout}>Sign Out</Btn>
      </div>
    </div>
  );
};
